package com.example.event_management_system.data.repository

import com.example.event_management_system.data.models.Location
import com.example.event_management_system.util.currentTimeMillis
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class LocationRepository(
    private val firestore: FirebaseFirestore
) : ILocationRepository {
    private val locationsCollection = firestore.collection("locations")

    override suspend fun getLocation(locationId: String): Location? {
        return try {
            val doc = locationsCollection.document(locationId).get()
            if (doc.exists) {
                doc.data<Location>().copy(id = doc.id)
            } else {
                null
            }
        } catch (e: Throwable) {
            null
        }
    }

    override fun getLocationFlow(locationId: String): Flow<Location?> {
        return locationsCollection.document(locationId).snapshots.map { doc ->
            if (doc.exists) {
                doc.data<Location>().copy(id = doc.id)
            } else {
                null
            }
        }
    }

    override fun getAllLocationsFlow(): Flow<List<Location>> {
        return locationsCollection
            .where { "isActive" equalTo true }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { doc ->
                    doc.data<Location>().copy(id = doc.id)
                }
            }
    }

    override fun getLocationsByIdsFlow(locationIds: List<String>): Flow<List<Location>> {
        if (locationIds.isEmpty()) {
            return kotlinx.coroutines.flow.flowOf(emptyList())
        }
        return locationsCollection
            .where { "isActive" equalTo true }
            .snapshots
            .map { snapshot ->
                snapshot.documents
                    .filter { doc -> doc.id in locationIds }
                    .map { doc -> doc.data<Location>().copy(id = doc.id) }
            }
    }

    override suspend fun createLocation(location: Location): String {
        val currentTime = currentTimeMillis()
        val locationWithTimestamp = location.copy(createdAt = currentTime)
        val docRef = locationsCollection.add(locationWithTimestamp)
        return docRef.id
    }

    override suspend fun updateLocation(locationId: String, location: Location) {
        locationsCollection.document(locationId).set(location)
    }

    override suspend fun deleteLocation(locationId: String) {
        locationsCollection.document(locationId).update("isActive" to false)
    }
}
